// org 환경 설정 — 업체명·권역·창고(지오코딩)·SMS 발신번호를 관리자가 수정
import { useEffect, useState } from "react";
import { doc, updateDoc, serverTimestamp, getDocs, writeBatch, deleteField } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { geocodeAddress } from "@/lib/vworld";
import { getCurrentOrgId, orgQuery } from "@/lib/org";
import { isInRegion, parseGuFrom } from "@/lib/region";
import { useOrgStore } from "@/store/org";
import { useAuth } from "@/store/auth";
import { Link } from "react-router-dom";
import { requestCancellation } from "@/lib/billing";
import { requestSenderRegistration } from "@/lib/senderRequest";

import { plainError } from "@/lib/plainError";
import { digitsOnly, isOwnerPhone, isGuardianContactPhone } from "@/lib/orgPhone";
const SIDO_LIST = [
  "서울특별시", "부산광역시", "대구광역시", "인천광역시", "광주광역시", "대전광역시", "울산광역시",
  "세종특별자치시", "경기도", "강원특별자치도", "충청북도", "충청남도", "전북특별자치도",
  "전라남도", "경상북도", "경상남도", "제주특별자치도",
];

export default function OrgSettings() {
  const org = useOrgStore((s) => s.org);
  const { user } = useAuth();
  // 사장님 연락처는 대표(업체를 만든 계정)만 바꾼다 — 대표 미지정 업체는 관리자 누구나(대표 결정 Q8, 저장 규칙도 같다)
  const canEditOwnerPhone = !org?.createdBy || org.createdBy === user?.uid;
  // 해지 신청·거두기도 대표만(대표 결정 Q8) — 서버 requestCancellation도 같은 검사
  const canCancel = canEditOwnerPhone;
  const [name, setName] = useState("");
  const [sido, setSido] = useState("");
  const [sigungu, setSigungu] = useState("");
  const [whAddr, setWhAddr] = useState("");
  const [sender, setSender] = useState("");
  const [bizNumber, setBizNumber] = useState("");
  const [ceoName, setCeoName] = useState("");
  const [taxEmail, setTaxEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // 발신번호 등록 요청(1호 업체 외)
  const [senderReq, setSenderReq] = useState("");
  const [senderMsg, setSenderMsg] = useState("");
  const requestSender = async () => {
    setBusy(true);
    setSenderMsg("");
    try {
      const r = await requestSenderRegistration(senderReq);
      setSenderMsg(r.notified ? "등록 요청을 받았습니다. 담당자가 서류를 안내해 드립니다." : "요청은 저장했지만 담당자에게 알림이 가지 않았습니다. 010-2608-5099로 전화 주세요.");
      setSenderReq("");
    } catch (e) {
      setSenderMsg(`실패 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  // 이용 그만두기
  const [cancelReason, setCancelReason] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelInfo, setCancelInfo] = useState<{ unpaid: { yearMonth: string; amount: number }[] } | null>(null);
  const [cancelMsg, setCancelMsg] = useState("");
  const cancelRequest = async (withdraw: boolean) => {
    if (!withdraw && !confirmCancel) { setConfirmCancel(true); return; }
    setConfirmCancel(false);
    setBusy(true);
    setCancelMsg("");
    try {
      const r = await requestCancellation(cancelReason, withdraw);
      setCancelInfo(withdraw ? null : { unpaid: r.unpaid });
      setCancelMsg(
        withdraw
          ? r.notified
            ? "해지 신청을 거두고 담당자에게 알렸습니다. 계속 이용하실 수 있습니다."
            : "해지 신청을 거두었지만 담당자에게 알림이 가지 않았습니다. 해지 절차가 진행되지 않게 010-2608-5099로 꼭 알려 주세요."
          : r.notified
            ? "해지 신청을 받았습니다. 담당자가 전화로 안내해 드립니다."
            : "신청은 저장했지만 담당자에게 알림이 가지 않았습니다. 번거로우시겠지만 010-2608-5099로 전화 주세요.",
      );
    } catch (e) {
      setCancelMsg(`실패 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  // org 문서 로드 시 폼 초기화(사용자 입력 중이 아닐 때만 1회)
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!org || seeded) return;
    setName(org.name || "");
    setSido(org.regions?.[0] || "");
    setSigungu((org.sigungu || []).join(", "));
    setWhAddr(org.warehouse?.label || "");
    setSender(org.solapiSender || "");
    setBizNumber(org.bizNumber || "");
    setCeoName(org.ceoName || "");
    setTaxEmail(org.taxEmail || "");
    setOwnerPhone(org.ownerPhone || "");
    setGuardianPhone(org.guardianContactPhone || "");
    setSeeded(true);
  }, [org, seeded]);

  // 담당 권역이 바뀐 뒤 학생 전체를 다시 판정한다. "이번 달만" 택배는 건드리지 않고, 권역 밖(항상)만 넣고 뺀다.
  const rejudgeRegion = async (cfg: { sido: string; sigungu: string[] }) => {
    const snap = await getDocs(orgQuery("students"));
    const toCourier: string[] = [];
    const toDirect: string[] = [];
    snap.forEach((d) => {
      const s = d.data() as { refinedAddress?: string; rawAddress?: string; gu?: string; courierFlag?: boolean; courierReason?: string; status?: string };
      const addr = `${s.refinedAddress || ""} ${s.rawAddress || ""}`;
      const inside = isInRegion(addr, s.gu || parseGuFrom(addr, cfg), cfg);
      if (!inside && !s.courierFlag) toCourier.push(d.id);
      else if (inside && s.courierFlag && s.courierReason === "always") toDirect.push(d.id);
    });
    if (!toCourier.length && !toDirect.length) { setMsg((m) => `${m} · 담당 지역이 바뀌었지만 택배 판정이 바뀌는 학생은 없습니다.`); return; }
    if (!window.confirm(`담당 지역이 바뀌어 학생 ${toCourier.length + toDirect.length}명의 택배 판정이 달라집니다.\n권역 밖 → 택배(항상) ${toCourier.length}명, 권역 안 → 직배송 ${toDirect.length}명. 지금 바꿀까요?`)) {
      setMsg((m) => `${m} · 택배 판정은 그대로 두었습니다(학생 관리에서 따로 바꿀 수 있습니다).`);
      return;
    }
    const all = [...toCourier.map((id) => ({ id, courier: true })), ...toDirect.map((id) => ({ id, courier: false }))];
    for (let i = 0; i < all.length; i += 450) {
      const b = writeBatch(db);
      all.slice(i, i + 450).forEach(({ id, courier }) =>
        b.update(doc(db, "students", id), courier ? { courierFlag: true, courierReason: "always", updatedAt: serverTimestamp() } : { courierFlag: false, courierReason: deleteField(), updatedAt: serverTimestamp() }));
      await b.commit();
    }
    setMsg(`✅ 저장 완료 · 택배 판정 다시 함 — 택배(항상) ${toCourier.length}명, 직배송 ${toDirect.length}명`);
  };

  const save = async () => {
    if (busy) return;
    // 시도를 비우면 화면·서버가 조용히 부산 기준으로 돌아간다 — 저장 전에 막는다
    if (!sido.trim()) { setMsg("담당 시·도를 골라 주세요. 비워 두면 주소를 부산 기준으로 읽습니다."); return; }
    // 비워 두는 것은 막지 않는다(첫 화면이 계속 알린다). 적었으면 형식만 본다
    if (canEditOwnerPhone && ownerPhone.trim() && !isOwnerPhone(ownerPhone)) { setMsg("사장님 휴대폰 번호를 010으로 시작하는 숫자로 넣어 주세요."); return; }
    if (guardianPhone.trim() && !isGuardianContactPhone(guardianPhone)) { setMsg("보호자 문의 번호를 지역번호부터 넣어 주세요(예: 051-123-4567)."); return; }
    setBusy(true);
    setMsg("저장 중…");
    try {
      // 창고 주소가 바뀌었으면 지오코딩으로 지도 위치 갱신 — 브라우저에서 직접(V-World는 서버 IP를 차단하므로 클라이언트 호출)
      let warehouse = org?.warehouse;
      if (whAddr.trim() && whAddr.trim() !== (org?.warehouse?.label || "")) {
        const p = await geocodeAddress(whAddr.trim()).catch(() => null);
        if (p) {
          warehouse = { lat: p.y, lng: p.x, label: whAddr.trim() };
        } else {
          setMsg("⚠️ 창고 주소 지도 위치를 못 찾음 — 주소를 확인하세요. (다른 항목은 저장됨)");
        }
      }
      await updateDoc(doc(db, "orgs", getCurrentOrgId()), {
        name: name.trim() || org?.name || "",
        regions: [sido.trim()],
        sigungu: sigungu.split(/[,\uFF0C\u3001\s]+/).map((s) => s.trim()).filter(Boolean),
        // 발신번호는 1호 업체만 화면에서 고친다 — 다른 업체는 저장 규칙이 막으므로 보내지 않는다(운영자가 등록 뒤 서버로 넣음)
        ...(getCurrentOrgId() === "freemilk" ? { solapiSender: sender.replace(/[^0-9]/g, "") } : {}),
        bizNumber: bizNumber.replace(/[^0-9-]/g, "").trim(),
        ceoName: ceoName.trim(),
        taxEmail: taxEmail.trim(),
        // 사장님 휴대폰은 대표만 — 대표가 아니면 보내지 않는다(저장 규칙도 막는다). 보호자 문의 번호는 관리자 누구나
        ...(canEditOwnerPhone ? { ownerPhone: digitsOnly(ownerPhone) } : {}),
        guardianContactPhone: digitsOnly(guardianPhone),
        ...(warehouse ? { warehouse } : {}),
        updatedAt: serverTimestamp(),
      });
      setMsg((m) => (m.startsWith("⚠️") ? m : "✅ 저장 완료"));
      // 권역이 바뀌었으면 이미 올린 학생의 택배(권역 밖) 판정을 다시 한다 — 안 하면 옛 판정이 그대로 남는다(3차 점검 11-#5)
      const newSigungu = sigungu.split(/[,\uFF0C\u3001\s]+/).map((s) => s.trim()).filter(Boolean);
      const regionChanged = (org?.regions?.[0] || "") !== sido.trim() || (org?.sigungu || []).join("|") !== newSigungu.join("|");
      if (regionChanged) await rejudgeRegion({ sido: sido.trim(), sigungu: newSigungu });
    } catch (e: any) {
      setMsg(`❌ 저장하지 못했습니다 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <h1 className="text-lg font-extrabold">⚙️ 환경 설정</h1>
      <div className="card p-5 space-y-4 text-sm">
        <div className="text-sm text-slate-500">업체 코드: <code>{getCurrentOrgId()}</code> (문의할 때 알려 주시면 빠릅니다)</div>
        <label className="block">
          <span className="font-semibold">업체명</span>
          <input className="input mt-1 w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block">
          <span className="font-semibold">담당 시·도</span>
          <select className="input mt-1 w-full" value={sido} onChange={(e) => setSido(e.target.value)}>
            <option value="">선택하세요</option>
            {SIDO_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs text-slate-400">주소를 지도에서 찾거나 자동으로 고칠 때 이 지역을 기준으로 봅니다.</span>
        </label>
        <label className="block">
          <span className="font-semibold">담당 시·군·구</span>
          <input className="input mt-1 w-full" placeholder="쉼표나 띄어쓰기로 구분 (예: 사상구, 사하구)" value={sigungu} onChange={(e) => setSigungu(e.target.value)} />
          <span className="text-xs text-slate-400">비워 두면 위 시·도 전체를 담당하는 것으로 봅니다. 적어 두면 다른 구·군 주소를 「택배」로 걸러 줍니다.</span>
        </label>
        <label className="block">
          <span className="font-semibold">창고(출발지) 주소</span>
          <input className="input mt-1 w-full" value={whAddr} onChange={(e) => setWhAddr(e.target.value)} />
          <span className="text-xs text-slate-400">
            현재 지도 위치: {org?.warehouse ? `${org.warehouse.lat.toFixed(4)}, ${org.warehouse.lng.toFixed(4)}` : "아직 없음"} — 저장 시 새 주소를 지도 위치로 변환합니다.
          </span>
        </label>
        <label className="block">
          <span className="font-semibold">보호자 알림 보내는 번호</span>
          {/* 문자 발송 업체(Solapi)에 등록된 번호만 보낼 수 있다 — 등록 안 된 번호를 넣으면 알림이 전부 실패하므로 1호 업체만 직접 고친다 */}
          <input
            className="input mt-1 w-full"
            placeholder="비워 두면 무상우유콕 대표 번호로 보냅니다"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            disabled={getCurrentOrgId() !== "freemilk"}
          />
          {getCurrentOrgId() === "freemilk" ? (
            <span className="text-sm text-slate-500">문자 발송 업체에 등록된 번호만 됩니다.</span>
          ) : (
            <div className="text-sm text-slate-600 space-y-2 mt-1">
              <p>
                지금은 <b>무상우유콕 대표 번호(010-2608-5099)</b>로 보냅니다. 업체 번호로 보내려면 문자 발송 업체에 번호를 먼저 등록해야 하는데, 통신사 서류가 필요해 저희가 대신 해 드립니다.
              </p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>아래에 보호자에게 보일 번호(휴대폰 또는 사무실 번호)를 적고 「등록 요청」을 누릅니다.</li>
                <li>담당자가 전화로 필요한 서류(통신사 가입증명원 등)를 안내하고, 사진으로 받아 등록합니다. 보통 2~3일 걸립니다.</li>
                <li>등록이 끝나면 이 칸에 번호가 채워지고, 그 뒤 보내는 알림부터 업체 번호로 나갑니다.</li>
              </ol>
              {org?.senderRequest?.phone && !org.solapiSender && (
                <p className="text-amber-700">등록 요청 접수됨: {org.senderRequest.phone} — 담당자가 연락드립니다.</p>
              )}
              <div className="flex gap-2 flex-wrap">
                <input className="input flex-1 min-w-[180px]" placeholder="등록할 번호(숫자만)" value={senderReq} onChange={(e) => setSenderReq(e.target.value)} inputMode="tel" />
                <button className="btn-secondary min-h-[44px]" onClick={requestSender} disabled={busy || !senderReq.trim()}>등록 요청</button>
              </div>
              {senderMsg && <div className="text-slate-600" role="status">{senderMsg}</div>}
            </div>
          )}
        </label>
        <div className="pt-2 border-t space-y-3">
          <div className="font-semibold">업체 연락처</div>
          {(!org?.ownerPhone || !org?.guardianContactPhone) && (
            <p className="text-amber-700">비어 있는 번호가 있습니다. 넣고 「저장」을 눌러 주세요.</p>
          )}
          <label className="block">
            <span className="font-semibold">사장님 휴대폰</span>
            <input className="input mt-1 w-full" placeholder="예: 010-1234-5678" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} inputMode="tel" disabled={!canEditOwnerPhone} />
            <span className="text-sm text-slate-500">이용료 청구 문자·긴급 연락·기사님 연락에 씁니다. 보호자에게는 보이지 않습니다.</span>
            {!canEditOwnerPhone && <span className="block text-xs text-slate-500 mt-1">사장님 휴대폰은 대표 계정(업체를 만든 계정)만 바꿀 수 있습니다.</span>}
          </label>
          <label className="block">
            <span className="font-semibold">보호자 문의 번호</span>
            <input className="input mt-1 w-full" placeholder="예: 051-123-4567 (사무실 번호도 됩니다)" value={guardianPhone} onChange={(e) => setGuardianPhone(e.target.value)} inputMode="tel" />
            <span className="text-sm text-slate-500">보호자가 보는 배송 사진 화면과 문자에 이 번호가 보입니다.</span>
          </label>
        </div>
        <div className="pt-2 border-t">
          <div className="font-semibold mb-1">세금계산서 받을 정보</div>
          <p className="text-sm text-slate-500 mb-2">이용료를 계좌이체로 내고 세금계산서를 받으실 때 씁니다. 카드로 내면 카드 영수증이 증빙입니다. 연락은 위 사장님 휴대폰으로 드립니다.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className="input" placeholder="사업자등록번호" value={bizNumber} onChange={(e) => setBizNumber(e.target.value)} inputMode="numeric" />
            <input className="input" placeholder="대표자 이름" value={ceoName} onChange={(e) => setCeoName(e.target.value)} />
            <input className="input" placeholder="계산서 받을 이메일" value={taxEmail} onChange={(e) => setTaxEmail(e.target.value)} inputMode="email" />
          </div>
        </div>
        {msg && <div className="text-slate-600">{msg}</div>}
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? "저장 중…" : "저장"}</button>
      </div>

      {/* 이용 그만두기 — 시즌이 끝나 그만둘 때 누를 곳(예행연습 03-11). 확정·자료 파기는 사람이 한다 */}
      <div className="card p-5 space-y-3 text-sm">
        <div className="font-semibold">이용 그만두기</div>
        {org?.cancelRequestedAt ? (
          <div className="rounded-xl bg-amber-50 p-3 space-y-2">
            <p>
              <b>해지 신청을 받았습니다</b>({org.cancelRequestedAt.toDate().toLocaleDateString("ko-KR")}). 담당자가 전화로 남은 요금과 자료 정리 일정을 안내해 드립니다.
              그때까지 앱은 그대로 쓰실 수 있습니다.
            </p>
            {cancelInfo?.unpaid.length ? (
              <p>아직 안 낸 이용료: {cancelInfo.unpaid.map((u) => `${u.yearMonth} ${u.amount.toLocaleString()}원`).join(", ")} — <Link className="underline" to="/admin/billing">이용료 화면에서 내기</Link></p>
            ) : null}
            <button className="btn-secondary" onClick={() => cancelRequest(true)} disabled={busy || !canCancel}>해지 신청 거두기</button>
            {!canCancel && <p className="text-xs text-slate-500">해지 신청을 거두는 일은 대표(업체를 만든 계정)만 할 수 있습니다.</p>}
          </div>
        ) : (
          <>
            <ol className="list-decimal pl-5 space-y-1 text-slate-700">
              <li>남은 이용료는 <Link className="underline" to="/admin/billing">이용료 화면</Link>에서 확인하고 내 주세요. 해지 뒤에도 끝난 달 요금은 청구됩니다.</li>
              <li>자료가 필요하면 먼저 받아 두세요. <Link className="underline" to="/admin/students">학생 관리</Link>의 「명단 내려받기」와 「배송 기록 내려받기」를 쓰시면 됩니다.</li>
              <li>해지가 확정되면 <b>30일 안에</b> 학생 명단·배송 사진·기록을 지웁니다(되돌릴 수 없습니다). 해지하지 않아도 학년도(3월~다음 해 2월) 배송이 끝난 명단은 그다음 3월 말까지 지웁니다.</li>
              <li>궁금한 점은 <a className="underline" href="tel:01026085099">010-2608-5099</a>(평일 9시~18시)로 전화 주세요.</li>
            </ol>
            <input className="input w-full" placeholder="그만두시는 이유(선택) — 예: 시즌 종료, 다른 프로그램 사용" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={200} />
            <button className={`btn-secondary ${confirmCancel ? "border-rose-400 text-rose-700" : ""}`} onClick={() => cancelRequest(false)} disabled={busy || !canCancel}>
              {confirmCancel ? "정말 해지를 신청합니다 — 한 번 더 누르세요" : "해지 신청"}
            </button>
            {!canCancel && <p className="text-xs text-slate-500">해지 신청은 대표(업체를 만든 계정)만 할 수 있습니다.</p>}
          </>
        )}
        {cancelMsg && <div className="text-slate-600" role="status">{cancelMsg}</div>}
      </div>
    </div>
  );
}
